import React, { useState, useMemo } from 'react';
import { Modal, Stack, Text, Select, Button, Divider } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { usePatientDelegations, useAvailableHcps } from '../../../../global/hooks/useHcpDelegation';

export interface IValidationResponsibilityResult {
    responsibility: 'self' | 'delegate';
    delegatedHcpId?: number;
}

interface IValidationResponsibilityDialogProps {
    opened: boolean;
    onClose: () => void;
    onConfirm: (result: IValidationResponsibilityResult) => void;
    patientId: number;
}

export const ValidationResponsibilityDialog: React.FC<IValidationResponsibilityDialogProps> = ({
    opened,
    onClose,
    onConfirm,
    patientId,
}) => {
    const { t } = useTranslation();
    const [selectedHcpId, setSelectedHcpId] = useState<string | null>(null);

    const { data: delegations = [] } = usePatientDelegations(patientId);
    const { data: availableHcps = [] } = useAvailableHcps();

    const selectData = useMemo(() => {
        const delegatedIds = new Set(delegations.map((d) => d.delegatedHcpID));

        const delegatedGroup = delegations.map((d) => ({
            value: String(d.delegatedHcpID),
            label: d.delegatedHcpName,
        }));

        const facilityGroup = availableHcps
            .filter((hcp) => hcp.belongsToCurrentFacilities && !delegatedIds.has(hcp.id))
            .map((hcp) => ({
                value: String(hcp.id),
                label: hcp.name,
            }));

        return [
            ...(delegatedGroup.length > 0
                ? [{ group: t('otp.validationResponsibility.groupHasAccess'), items: delegatedGroup }]
                : []),
            ...(facilityGroup.length > 0
                ? [{ group: t('otp.validationResponsibility.groupFacility'), items: facilityGroup }]
                : []),
        ];
    }, [delegations, availableHcps, t]);

    const handleClose = () => {
        setSelectedHcpId(null);
        onClose();
    };

    const handleSelfResponsible = () => {
        setSelectedHcpId(null);
        onConfirm({ responsibility: 'self' });
    };

    const handleDelegate = () => {
        if (!selectedHcpId) return;
        onConfirm({ responsibility: 'delegate', delegatedHcpId: Number(selectedHcpId) });
        setSelectedHcpId(null);
    };

    return (
        <Modal
            opened={opened}
            onClose={handleClose}
            title={t('otp.validationResponsibility.title')}
            size="md"
        >
            <Stack gap="md">
                <Text size="sm" c="dimmed">
                    {t('otp.validationResponsibility.description')}
                </Text>

                <Button
                    fullWidth
                    variant="light"
                    color="blue"
                    onClick={handleSelfResponsible}
                >
                    {t('otp.validationResponsibility.selfButton')}
                </Button>

                <Divider label={t('otp.validationResponsibility.or')} labelPosition="center" />

                <Select
                    searchable
                    label={t('otp.validationResponsibility.selectHcp')}
                    placeholder={t('otp.validationResponsibility.selectHcpPlaceholder')}
                    data={selectData}
                    value={selectedHcpId}
                    onChange={setSelectedHcpId}
                />

                <Button
                    fullWidth
                    color="blue"
                    disabled={!selectedHcpId}
                    onClick={handleDelegate}
                >
                    {t('otp.validationResponsibility.delegateButton')}
                </Button>

                <Button variant="subtle" color="gray" onClick={handleClose}>
                    {t('common.cancel')}
                </Button>
            </Stack>
        </Modal>
    );
};
